import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { COMPLETE_COURSE, GET_MY_COURSE_COMPLETIONS } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { useCurrentUser } from "./useCurrentUser";
import { useReactQueryErrorReporter } from "./use-error-reporter";

interface CourseCompletion {
  id: string;
  completedAt: string;
  path: string;
  shardID: string;
  courseID: string;
}

export const useGetMyCourseCompletions = () => {
  const { error, data, ...rest } = useQuery({
    queryKey: ["courseCompletions"],
    queryFn: async () => {
      const resp = await graphQLClient.request(GET_MY_COURSE_COMPLETIONS);
      return (resp as { getMyCourseCompletions: CourseCompletion[] })
        .getMyCourseCompletions;
    },
  });

  useReactQueryErrorReporter("Error fetching course completions", error, false);

  return { ...rest, error, data: data || [], loading: rest.isLoading };
};

export const useCompleteCourse = () => {
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ courseID }: { courseID: string }) => {
      const { completeCourse } = (await graphQLClient.request(COMPLETE_COURSE, {
        input: {
          courseID,
          shardID: currentUser.shardID,
          userPath: currentUser.userPath,
        },
      })) as { completeCourse: CourseCompletion };
      return completeCourse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["courseCompletions"] });
    },
  });

  return { completeCourse: mutation };
};
